import { useEffect } from 'react';
import { useOrganisation } from '../api/hooks.js';
import { useAuthStore } from '../auth/store.js';

/**
 * Per-organisation white-label branding, resolved for the chrome (§20.5).
 *
 * An agency reselling INTEGRA presents each client's own identity, not the
 * Fruitful Bough estate brand. This hook reads the signed-in organisation's
 * branding and does two things:
 *
 *  1. Injects the org's colours as CSS variables onto :root via the DOM
 *     (`document.documentElement.style`), so every `*-brand-primary` /
 *     `*-brand-accent` utility recolours to the org. This is DATA painted onto
 *     a variable, deliberately NOT a JSX `style={{}}` inline design value —
 *     which §21.4 (enforced by tests/integrity) forbids in components.
 *  2. Returns the product name / tagline / logo the lockup should show.
 *
 * With no override set (or signed out), everything falls back to the estate
 * brand, so existing organisations are visually unchanged.
 */
export interface ResolvedBrand {
  /** Product name for the lockup and aria labels; 'INTEGRA' by default. */
  readonly productName: string;
  /** Attribution line under the name; null hides it (a white-label org with no tagline). */
  readonly tagline: string | null;
  /** Logo image URL, or null to show the INTEGRA mark. */
  readonly logoUrl: string | null;
}

const ESTATE_PRODUCT_NAME = 'INTEGRA';
const ESTATE_TAGLINE = 'Fruitful Bough';

export function useBrandTheme(): ResolvedBrand {
  const organisationId = useAuthStore((s) => s.session?.organisationId ?? '');
  const { data: organisation } = useOrganisation(organisationId);
  const branding = organisation?.branding;

  const primaryColour = branding?.primaryColour ?? null;
  const accentColour = branding?.accentColour ?? null;

  // Paint the org's colours onto the document root as variables. Removing a
  // property (rather than setting a literal) lets the token default in
  // tokens.css take over, so clearing a colour returns to the estate brand.
  useEffect(() => {
    const root = document.documentElement;
    if (primaryColour !== null) root.style.setProperty('--brand-primary', primaryColour);
    else root.style.removeProperty('--brand-primary');
    if (accentColour !== null) root.style.setProperty('--brand-accent', accentColour);
    else root.style.removeProperty('--brand-accent');
    return () => {
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-accent');
    };
  }, [primaryColour, accentColour]);

  const whiteLabelled = branding?.productName != null && branding.productName !== '';
  if (whiteLabelled) {
    return {
      productName: branding.productName as string,
      tagline: branding.tagline,
      logoUrl: branding.logoUrl,
    };
  }
  return {
    productName: ESTATE_PRODUCT_NAME,
    tagline: branding?.tagline ?? ESTATE_TAGLINE,
    logoUrl: branding?.logoUrl ?? null,
  };
}
